"use client";

import { useEffect, useRef } from "react";
import { createClient } from "@/lib/supabase/client";
import { trpc } from "@/lib/trpc/react";
import { useSupabase } from "./supabase-auth-provider";

interface RealtimeUserSyncProviderProps {
	children: React.ReactNode;
}

// Force refetch on all mounted observers - web also needs this for SPAs
const FORCE_REFETCH = { refetchType: 'all' as const };

/**
 * Central realtime sync provider that listens to ALL user data changes
 * and invalidates the relevant React Query caches.
 *
 * This ensures that profile, discovery facepiles, and set cards
 * all stay in sync when user data changes - across tabs, devices, and components.
 *
 * Tables monitored:
 * - festival_attendance (user's festival status: going, want_to_go, etc.)
 * - show_attendance (user's show status)
 * - set_reviews (user's set reviews + priorities: want_to_see, must_see)
 * - show_reviews (user's show reviews)
 * - festival_reviews (user's festival reviews)
 */
export function RealtimeUserSyncProvider({
	children,
}: RealtimeUserSyncProviderProps) {
	const { user } = useSupabase();
	const utils = trpc.useUtils();
	const channelRef = useRef<ReturnType<
		ReturnType<typeof createClient>["channel"]
	> | null>(null);

	useEffect(() => {
		if (!user?.id) {
			// Clean up any existing channel when user logs out
			if (channelRef.current) {
				const supabase = createClient();
				supabase.removeChannel(channelRef.current);
				channelRef.current = null;
			}
			return;
		}

		const userId = user.id;
		const supabase = createClient();

		// Create a single channel for all user data subscriptions
		const channel = supabase
			.channel(`user-sync:${userId}`)
			// Festival attendance changes (going, want_to_go, etc.)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "festival_attendance",
					filter: `user_id=eq.${userId}`,
				},
				async (payload) => {
					console.log(
						"[RealtimeSync] Festival attendance changed:",
						payload.eventType,
					);

					// Invalidate all festival-related caches with forced refetch
					await Promise.all([
						// Profile festivals list - force refetch for mounted components
						utils.user.getProfileFestivals.invalidate(undefined, FORCE_REFETCH),
						// Profile artists (derived from festival attendance)
						utils.user.getProfileArtists.invalidate(undefined, FORCE_REFETCH),
						// User stats
						utils.user.getUserStats.invalidate({ userId }),
						// Discovery facepiles
						utils.festival.list.invalidate(),
						utils.festival.getFacepileData.invalidate(),
						// Individual festival user data
						utils.festival.getUserFestivalData.invalidate(),
						// Festival detail page status button
						utils.festival.getStatus.invalidate(undefined, FORCE_REFETCH),
					]);
				},
			)
			// Show attendance changes
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "show_attendance",
					filter: `user_id=eq.${userId}`,
				},
				async (payload) => {
					console.log(
						"[RealtimeSync] Show attendance changed:",
						payload.eventType,
					);

					// Invalidate all show-related caches with forced refetch
					await Promise.all([
						// Profile shows list - force refetch for mounted components
						utils.user.getProfileShows.invalidate(undefined, FORCE_REFETCH),
						// Profile artists (derived from show attendance)
						utils.user.getProfileArtists.invalidate(undefined, FORCE_REFETCH),
						// User stats
						utils.user.getUserStats.invalidate({ userId }),
						// Discovery facepiles
						utils.show.list.invalidate(),
						utils.show.getFacepileData.invalidate(),
						// Show detail page status button
						utils.show.getStatus.invalidate(undefined, FORCE_REFETCH),
					]);
				},
			)
			// Set reviews changes (includes priorities: want_to_see, must_see)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "set_reviews",
					filter: `user_id=eq.${userId}`,
				},
				async (payload) => {
					console.log("[RealtimeSync] Set review changed:", payload.eventType);

					// Invalidate set review caches with forced refetch
					await Promise.all([
						// Profile sets list - force refetch for mounted components
						utils.user.getProfileSets.invalidate(undefined, FORCE_REFETCH),
						// Profile artists (ratings affect artist data)
						utils.user.getProfileArtists.invalidate(undefined, FORCE_REFETCH),
						// User stats
						utils.user.getUserStats.invalidate({ userId }),
						// Festival user data (includes reviews)
						utils.festival.getUserFestivalData.invalidate(),
						// Set detail page user review
						utils.festival.getUserSetReview.invalidate(undefined, FORCE_REFETCH),
						utils.festival.getUserSetReviews.invalidate(undefined, FORCE_REFETCH),
					]);
				},
			)
			// Show reviews changes
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "show_reviews",
					filter: `user_id=eq.${userId}`,
				},
				async (payload) => {
					console.log("[RealtimeSync] Show review changed:", payload.eventType);

					// Invalidate show review caches with forced refetch
					await Promise.all([
						// Profile shows list - force refetch for mounted components
						utils.user.getProfileShows.invalidate(undefined, FORCE_REFETCH),
						// Profile artists
						utils.user.getProfileArtists.invalidate(undefined, FORCE_REFETCH),
						// User stats
						utils.user.getUserStats.invalidate({ userId }),
						// Show detail page user review
						utils.show.getUserShowReview.invalidate(undefined, FORCE_REFETCH),
					]);
				},
			)
			// Festival reviews changes
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "festival_reviews",
					filter: `user_id=eq.${userId}`,
				},
				async (payload) => {
					console.log(
						"[RealtimeSync] Festival review changed:",
						payload.eventType,
					);

					// Invalidate festival review caches with forced refetch
					await Promise.all([
						// Profile festivals list - force refetch for mounted components
						utils.user.getProfileFestivals.invalidate(undefined, FORCE_REFETCH),
						// User stats
						utils.user.getUserStats.invalidate({ userId }),
						// Festival user data
						utils.festival.getUserFestivalData.invalidate(),
					]);
				},
			)
			.subscribe((status) => {
				if (status === "SUBSCRIBED") {
					console.log("[RealtimeSync] Connected to user sync channel");
				} else if (status === "CHANNEL_ERROR") {
					console.warn("[RealtimeSync] Channel error, will retry on reconnect");
				} else if (status === "TIMED_OUT") {
					console.warn("[RealtimeSync] Connection timeout, will retry");
				}
			});

		channelRef.current = channel;

		return () => {
			if (channelRef.current) {
				console.log("[RealtimeSync] Cleaning up user sync channel");
				supabase.removeChannel(channelRef.current);
				channelRef.current = null;
			}
		};
	}, [user?.id, utils]);

	return <>{children}</>;
}
